/** PD-1651 — the long-form date a referral letter or fax cover sheet is dated with. */
export function referralLetterDate(now: Date): string {
  return now.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

/**
 * PD-1651 — "NPI 1234567890 · Org Name" line under a referral letter's closing
 * signature. Either half is dropped when the chart doesn't have it.
 */
export function referralClosingDetail(provider: { npi: string; organizationName: string }): string {
  return [provider.npi && `NPI ${provider.npi}`, provider.organizationName]
    .filter(Boolean)
    .join(' · ')
}

/** Converts a URL (e.g. /logo.png) to a data URL for pdfmake image embedding. */
export async function urlToDataURL(url: string, headers?: Record<string, string>): Promise<string> {
  const res = await fetch(url, { cache: 'force-cache', headers })
  if (!res.ok) {
    throw new Error(`Failed to load image at "${url}": HTTP ${res.status}`)
  }
  const blob = await res.blob()
  return await new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      if (typeof reader.result === 'string') resolve(reader.result)
      else reject(new Error('FileReader did not return a string'))
    }
    reader.onerror = () => reject(reader.error ?? new Error(`Failed to read image at "${url}"`))
    reader.readAsDataURL(blob)
  })
}

/** Triggers a browser download of the given PDF blob. */
export function downloadPdfBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

/** Converts Uint8Array PDF bytes to a base64 string for API transmission. */
export function pdfBytesToBase64(bytes: Uint8Array): string {
  const CHUNK = 8192
  // Build a binary string by splitting bytes into fixed-size chunks and converting each to characters
  const binary = Array.from(
    // Create an iterable of length ⌈totalBytes / chunkSize⌉ — one slot per chunk
    { length: Math.ceil(bytes.length / CHUNK) },
    // Map each chunk index to a string of characters from the corresponding byte slice
    (_, i) => String.fromCharCode(...bytes.subarray(i * CHUNK, (i + 1) * CHUNK)),
  ).join('')
  return btoa(binary)
}
